import { Temporal } from 'temporal-polyfill';

import { Pack } from '@/components/app/cards/PacksList/types';
import { useUserState } from '@/hooks/store';

interface UnlockInputs {
	isUnlocked?: boolean;
	isSubscribed: boolean;
	packType?: string;
	// Pack-level release date wins over the item's own, matching the mobile apps.
	releaseDate?: string;
	now?: Temporal.Instant;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// A date-only release is the start of that day in UTC, and the item is released from that instant on.
const releaseInstant = (releaseDate: string): Temporal.Instant =>
	DATE_ONLY.test(releaseDate)
		? Temporal.PlainDate.from(releaseDate).toZonedDateTime('UTC').toInstant()
		: Temporal.Instant.from(releaseDate);

const isReleaseInFuture = (releaseDate: string, now: Temporal.Instant): boolean => {
	try {
		return Temporal.Instant.compare(releaseInstant(releaseDate), now) > 0;
	} catch {
		return false;
	}
};

export const isUnlockedForUser = ({
	isUnlocked,
	isSubscribed,
	packType,
	releaseDate,
	now = Temporal.Now.instant(),
}: UnlockInputs): boolean => {
	if (!(isUnlocked || isSubscribed)) {
		return false;
	}
	if (packType === 'episodic' && releaseDate && isReleaseInFuture(releaseDate, now)) {
		return false;
	}
	return true;
};

export const useIsUnlockedForUser = () => {
	const isSubscribed = Boolean(useUserState()?.subscription?.valid);

	return (item: { is_unlocked?: boolean; release_date?: string }, pack?: Pack): boolean =>
		isUnlockedForUser({
			isUnlocked: item.is_unlocked,
			isSubscribed,
			packType: pack?.pack_type,
			releaseDate: pack?.release_date ?? item.release_date,
		});
};
